import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Popover from '@rsos/components/capstone/base/Popover';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { getMostRecentLocations } from '../../../utils/getMostRecentLoc';
import { APPLE, GOOGLE } from '../../Location/constants/dataSources';
import { getDataSource } from '../../Location/helpers';
import ASCButton from './ASCButton';
import {
  ShareTooltipContainer,
  ShareTooltipText,
} from './AgencyShareChatButton.styles';

// The popover is displayed beneath the agency share/chat button.
const popoverOffset = {
  x: 5,
  y: 30,
};

const tooltipText =
  'Sharing not supported for this call type. Our teams are working to enable sharing from other sources in a future release.';

/**
 * Sub-component of <AgencyShareChatButton />.
 * Renders the agency share/chat popover when in disabled state.
 * @param {Function} onClick - Handler when the button is clicked.
 * @param {Boolean} selected - Is the button selected.
 */
const ASCPopover = ({ onClick, selected }) => {
  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );
  const filteredCalls = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const selectedCall = filteredCalls?.[selectedCallerID];

  const {
    mostRecentLocation: mostRecentPrimaryLocation,
  } = getMostRecentLocations(selectedCall);

  const dataSource = getDataSource(mostRecentPrimaryLocation);

  const isTT911 = !!selectedCall.isTT911;

  const [isButtonHovered, setIsButtonHovered] = useState(false);

  // Agency share/chat button is disabled if the 911 event is text-to-911 or if the data source is
  // not Apple or Google.
  const isButtonDisabled = useMemo(() => {
    return isTT911 || (dataSource !== APPLE && dataSource !== GOOGLE);
  }, [dataSource, isTT911]);

  // Agency share/chat popover is visible when the share button is hovered while in the disabled
  // state.
  const isPopoverVisible = useMemo(() => {
    return !!(isButtonDisabled && isButtonHovered);
  }, [isButtonDisabled, isButtonHovered]);

  return (
    <Popover
      isOpen={isPopoverVisible}
      popoverID="agency-share-chat-tooltip"
      trigger={
        <ASCButton
          isDisabled={isButtonDisabled}
          isSelected={selected}
          onClick={onClick}
          onHover={setIsButtonHovered}
        />
      }
      useCustomOffset
      xOffset={popoverOffset.x}
      yOffset={popoverOffset.y}
    >
      <ShareTooltipContainer>
        <ShareTooltipText>{tooltipText}</ShareTooltipText>
      </ShareTooltipContainer>
    </Popover>
  );
};

ASCPopover.propTypes = {
  onClick: PropTypes.func,
  selected: PropTypes.bool,
};

export default ASCPopover;
